import { useState } from 'react';
import { Typography } from 'src/components/new/Typography';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AuditLogEntry from './AuditLogEntry';

export const formatPacificDate = (dateStr: string): string => {
  const date = new Date(dateStr);

  if (isNaN(date.getTime())) return 'Invalid Date';

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const partMap = Object.fromEntries(formatter.formatToParts(date).map((p) => [p.type, p.value]));

  return `${partMap.month}/${partMap.day}/${partMap.year}, ${partMap.hour}:${partMap.minute}:${partMap.second} PT`;
};

export interface DocumentSnapshot {
  filename: string;
  type: string;
  description: string;
  ecfReference: string;
  pageCount: number;
  position: number;
  paperColor?: string;
}

export interface AuditLogState {
  documents: DocumentSnapshot[];
  invoiceTotal: number;
  /** Present only for service_type_and_invoice edits */
  service?: string;
}

export interface EditAuditLogEntry {
  id: number;
  adminFirstName: string;
  adminLastName: string;
  adminEmail?: string;
  editType: string;
  previousState: AuditLogState;
  newState: AuditLogState;
  createdAt: string;
}

interface ChainOfCustodyProps {
  userId: number;
  ip: string;
  host: string;
  userAgent: string;
  createdAt: string;
  statusHistory:
  | {
    createdAt: string;
    status: string;
  }[]
  | null;
  editAuditLogs?: EditAuditLogEntry[] | null;
  isCosMember?: boolean;
}

const jobStatusMapper = {
  submitted: 'Submitted',
  on_hold: 'On Hold',
  in_queue: 'In Queue',
  processing: 'Processing',
  completed: 'Completed',
  cancelled: 'Cancelled',
  printing: 'Printing',
  completed_payment_declined: 'Complete - payment declined',
  draft: 'Draft',
  manual_review: 'Manual Review'
}

const INITIAL_VISIBLE_COUNT = 2;

type TimelineItem =
  | { type: 'status'; createdAt: string; status: string; index: number }
  | { type: 'auditGroup'; createdAt: string; logs: EditAuditLogEntry[] };

const ChainOfCustody = ({
  userId,
  ip,
  host,
  userAgent,
  statusHistory,
  editAuditLogs = null,
  isCosMember = false,
}: ChainOfCustodyProps) => {
  const [showAllEdits, setShowAllEdits] = useState(false);

  const sectionTitle = isCosMember ? 'Internal Team Edits' : 'CoS Team Edits';

  // Build merged timeline: status entries + audit log groups interleaved by time
  const buildTimeline = (): TimelineItem[] => {
    const statusItems: TimelineItem[] = (statusHistory || []).map((item, index) => ({
      type: 'status' as const,
      createdAt: item.createdAt,
      status: item.status,
      index: index + 1,
    }));

    if (!editAuditLogs || editAuditLogs.length === 0) {
      return statusItems;
    }

    // Group consecutive audit logs that fall between the same two status entries
    // Sort audit logs chronologically (ascending)
    const sortedAuditLogs = [...editAuditLogs].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    // Find where to insert audit log groups in the timeline
    // Group all audit logs that occur between two consecutive status entries
    const groups: { logs: EditAuditLogEntry[]; latestTimestamp: string }[] = [];
    let currentGroup: EditAuditLogEntry[] = [];

    for (const log of sortedAuditLogs) {
      currentGroup.push(log);
    }

    if (currentGroup.length > 0) {
      // Use the latest log's timestamp for positioning
      const latestTimestamp = currentGroup[currentGroup.length - 1].createdAt;
      groups.push({ logs: currentGroup, latestTimestamp });
    }

    // Merge status items and audit groups by timestamp
    const allItems: TimelineItem[] = [...statusItems];

    for (const group of groups) {
      const groupTime = new Date(group.latestTimestamp).getTime();

      // Find the insertion point: after the last status entry that is <= groupTime
      let insertIndex = 0;
      for (let i = 0; i < allItems.length; i++) {
        const item = allItems[i];
        if (new Date(item.createdAt).getTime() <= groupTime) {
          insertIndex = i + 1;
        }
      }

      allItems.splice(insertIndex, 0, {
        type: 'auditGroup',
        createdAt: group.latestTimestamp,
        logs: group.logs,
      });
    }

    return allItems;
  };

  const timeline = buildTimeline();

  // For numbering status entries sequentially
  let statusCounter = 0;

  return (
    <>
      <Typography
        sx={{
          marginTop: '0.9375rem',
          marginBottom: '0.9375rem',
          fontWeight: '600',
        }}
      >
        Chain of Custody
      </Typography>
      <div
        style={{
          padding: '0.9375rem',
          border: '0.0625rem solid #cac4d0',
          borderRadius: '0rem',
        }}
      >
        <Typography sx={{ marginBottom: '0.75rem' }}>
          <span style={{ fontWeight: '500' }}>Account: </span> {userId}
        </Typography>
        <Typography sx={{ marginBottom: '0.75rem' }}>
          <span style={{ fontWeight: '500' }}>IP Address: </span> {ip}
        </Typography>
        <Typography sx={{ marginBottom: '0.75rem' }}>
          <span style={{ fontWeight: '500' }}>Host: </span> {host}
        </Typography>
        <Typography sx={{ marginBottom: '0.75rem', width: '25rem' }}>
          <span style={{ fontWeight: '500' }}>Agent: </span> {userAgent}
        </Typography>
        <Typography
          sx={{
            marginBottom: '0.75rem',
            marginTop: '0.75rem',
            fontWeight: '500',
          }}
        >
          Timeline
        </Typography>

        {timeline.map((item, idx) => {
          if (item.type === 'status') {
            statusCounter++;
            return (
              <Typography key={`status-${idx}`} sx={{ marginBottom: '0.75rem', paddingLeft: '0.9375rem' }}>
                <span style={{ fontWeight: '500' }}>
                  {statusCounter}. {jobStatusMapper[item.status as keyof typeof jobStatusMapper]}:{' '}
                </span>
                {formatPacificDate(item.createdAt)}
              </Typography>
            );
          }

          if (item.type === 'auditGroup') {
            const logs = item.logs;
            // Sort descending (most recent first) for display
            const sortedLogs = [...logs].sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            const latestUpdate = sortedLogs[0]?.createdAt;
            const hasMoreThanTwo = sortedLogs.length > INITIAL_VISIBLE_COUNT;
            const visibleLogs = hasMoreThanTwo && !showAllEdits
              ? sortedLogs.slice(0, INITIAL_VISIBLE_COUNT)
              : sortedLogs;

            return (
              <div
                key={`audit-${idx}`}
                style={{
                  backgroundColor: '#f5f8ff',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  marginTop: '0.5rem',
                  marginBottom: '0.75rem',
                }}
              >
                {/* Section header */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', gap: '0.5rem' }}>
                  <Typography sx={{ fontWeight: '600', textDecoration: 'underline' }}>
                    {sectionTitle}
                  </Typography>
                  <Typography sx={{ color: '#666', fontWeight: '400' }}>
                    Latest Update {latestUpdate ? formatPacificDate(latestUpdate) : ''}
                  </Typography>
                  <div style={{ flex: 1 }} />
                  {isCosMember && (
                    <Tooltip
                      title='Team member names appear in the internal Chain of Custody only. Customers see "CoS Team" in their view.'
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: { fontSize: '0.875rem', padding: '0.5rem 0.75rem' },
                        },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: '1.125rem', color: '#666', cursor: 'pointer' }} />
                    </Tooltip>
                  )}
                </div>

                {/* Audit log entries */}
                <div style={{ paddingLeft: '0.25rem' }}>
                  {visibleLogs.map((entry) => (
                    <AuditLogEntry key={entry.id} entry={entry} isCosMember={isCosMember} />
                  ))}
                </div>

                {/* Show More / Show Less button */}
                {hasMoreThanTwo && (
                  <button
                    onClick={() => setShowAllEdits(!showAllEdits)}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      border: '1px solid #436EB2',
                      borderRadius: '0.25rem',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '0.875rem',
                      color: '#436EB2',
                      marginTop: '0.5rem',
                    }}
                  >
                    {showAllEdits ? 'Show Less Edits' : 'Show More Edits'}
                  </button>
                )}
              </div>
            );
          }

          return null;
        })}
      </div>
    </>
  );
};

export default ChainOfCustody;
